<template>
  <div class="choice-item container-fluid" :class="choiceScaleClass">
    <div class="row">
      <div class="col-xs-9">
        <div class="choice-polarity" v-if="!isEditing">{{ polarityIndicator }}</div>
        <span class="choice-title">
          <span v-show="!isEditing || !isEditable">{{ choice.title }}</span>
          <span v-show="isEditing && isEditable">
            <el-input
              class="choice-editor"
              ref="choiceInput"
              size="mini"
              :autofocus="true"
              :focus="true"
              v-model="editTitle"
              @keyup.native.enter="handleUpdateChoice"
              @blur="handleInputBlur"
            ></el-input>
          </span>
        </span>
      </div>
      <div class="col-xs-3" v-if="isEditable">
        <span class="choice-tools pull-right">
          <span class="conditional-link" style="text-align: right" v-if="isConditional">
            <conditional-link-select
              v-model="computedConditionalLink"
              :excludeQuestions="[question.id]"
            ></conditional-link-select>
          </span>
          <span>
            <el-tooltip content="Conditional link">
              <i class="fa fa-fw fa-link" @click="handleToggleConditional"></i>
            </el-tooltip>
          </span>

          <span class="tool-divider"></span>

          <el-tooltip content="Edit">
            <i class="fa fa-fw fa-pencil" @click="handleEdit"></i>
          </el-tooltip>
          <el-tooltip content="Remove">
            <i class="fa fa-fw fa-trash" @click="handleRemove"></i>
          </el-tooltip>
          <span class="tool-divider"></span>
          <span class="handle">
            <i class="fa fa-fw fa-navicon"></i>
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import ConditionalLinkSelect from '@/components/surveybuilder/editors/ConditionalLinkSelect';

export default {
  name: 'ScaleChoiceItem',
  components: {
    ConditionalLinkSelect,
  },
  props: {
    choice: {
      type: Object,
      required: true,
    },
    question: {
      type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    editTitle: null,
    isEditing: false,
    isConditional: false,
    conditionalLink: null,
  }),
  mounted() {
    this.editTitle = this.choice.title;
    this.isEditing = this.choice.title === '';

    if (this.choice.conditionalLink) {
      this.conditionalLink = this.choice.conditionalLink;
      this.isConditional = true;
    }

    this.$nextTick(() => this.$refs.choiceInput.focus());
  },
  computed: {
    computedConditionalLink: {
      get() {
        return this.conditionalLink;
      },
      set(value) {
        this.conditionalLink = value;
        this.handleUpdateChoice();
      },
    },

    choiceScaleClass() {
      const polarity = this.choice.polarity || 0;
      if (polarity > 0) {
        return 'positive';
      } else if (polarity < 0) {
        return 'negative';
      }
      return 'neutral';
    },

    polarityIndicator() {
      const polarity = this.choice.polarity || 0;
      if (polarity > 0) {
        return '+'.repeat(polarity);
      } else if (polarity < 0) {
        return '-'.repeat(polarity * -1);
      }
      return '';
    },
  },
  methods: {
    handleEdit() {
      this.isEditing = !this.isEditing;

      if (this.isEditing) {
        this.$refs.choiceInput.focus();
      } else {
        this.handleUpdateChoice();
      }
    },

    handleRemove() {
      this.$emit('onRemove', this.choice);
    },

    handleUpdateChoice() {
      if (this.editTitle === '') {
        this.handleRevertUpdate();
      } else {
        const choice = {
          ...this.choice,
          title: this.editTitle,
          conditionalLink: this.conditionalLink,
        };
        this.$emit('onUpdateChoice', choice);
        this.isEditing = false;
      }
    },

    handleRevertUpdate() {
      this.editTitle = this.choice.title;
      this.isEditing = false;
    },

    handleInputBlur() {
      if (isNaN(this.choice.id) && this.editTitle === '') {
        this.$emit('onEmpty');
      } else {
        this.handleUpdateChoice();
      }
    },

    handleToggleConditional() {
      this.conditionalLink = null;
      this.isConditional = !this.isConditional;

      // Did we remove the conditional?
      if (!this.isConditional) {
        this.handleUpdateChoice();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/style/_variables_live.scss';

.choice-item {
  width: 100%;
  margin-bottom: 4px;
  padding: 8px;
  border-radius: 3px;
}

.choice-editor {
  margin: -5px;
}

.choice-tools {
  cursor: pointer;
  white-space: nowrap;
}

.tool-divider {
  display: inline-block;
  width: 1px;
  height: 1em;
  margin: 0 8px;
  vertical-align: middle;
  position: relative;
}

.choice-polarity {
  position: absolute;
  margin-top: -9px;
  font-size: 10px;
  font-family: monospace;
}

.handle {
  cursor: grab;
}

.positive {
  background-color: lighten($colony-green, 35%);
  border: 1px solid $colony-green;
}

.positive .choice-tools,
.positive .choice-polarity {
  color: $colony-green;
}

.positive .tool-divider {
  background-color: $colony-green;
}

.neutral {
  background-color: lighten($colony-orange, 35%);
  border: 1px solid $colony-orange;
}

.neutral .choice-tools,
.neutral .choice-polarity {
  color: $colony-orange;
}

.neutral .tool-divider {
  background-color: $colony-orange;
}

.negative {
  background-color: lighten($colony-red, 25%);
  border: 1px solid $colony-red;
}

.negative .choice-tools,
.negative .choice-polarity {
  color: lighten($colony-red, 10%);
}

.negative .tool-divider {
  background-color: lighten($colony-red, 10%);
}
</style>



// WEBPACK FOOTER //
// ScaleChoiceItem.vue?f8493ae4